import React, { AllHTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Box, Chip, Divider, Grid, IconButton, Stack, Theme, Typography, useTheme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import CancelRounded from '@mui/icons-material/CancelRounded'
import DeleteIcon from '@mui/icons-material/Delete'
import WarningIcon from '@mui/icons-material/Warning'
import TaskAltIcon from '@mui/icons-material/TaskAlt'

import { getUser, namedRequestInProgress } from '../../../../../store/common/common.selectors'
import { NetworkPage, ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import ImportProfessocialPost from '../../../ImportComponents/ImportProfessocialPost'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import { useTranslation } from 'react-i18next'
import {
  getAboTopics,
  getCalendarData,
  getFacebookPage,
  getInstagramPage,
  selectFacebookPostByPostId,
  selectInstagramPostByPostId,
} from '../../../../../store/dashboard/dashboard.selector'
import {
  AboType,
  CalendarData,
  DeferredPost,
  DeferredPostSource,
  DeferredPostStatus,
  FacebookPage,
  FacebookPost,
  InstagramPage,
  InstagramPost,
  NetworkMap,
  NetworkType,
  PostTemplate,
  PublishedPostWithPostContent,
  TopicsWithMetadataResponse,
} from '@3m5/sm_publish-frontend-api'
import Statistic from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/Statistic'
import EditIcon from '@mui/icons-material/Edit'
import ClearIcon from '@mui/icons-material/Clear'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { DeletePostTemplateRequest } from '@3m5/sm_publish-frontend-api'
import {
  confirmManualDeferredPostInitiated,
  declineManualDeferredPostInitiated,
  deleteManualDeferredPostInitiated,
  deletePostInitiated,
  deletePostTemplateInitiated,
  getFacebookPostInitiated,
  getInstagramPostInitiated,
  publishPostInitiated,
  retryManualDeferredPostInitiated,
} from '../../../../../store/dashboard/dashboard.actions'
import { useHistory } from 'react-router-dom'
import { CoreRootState } from '../../../../../store/coreRoot.reducer'
import { getFacebookConnectDataInitiated } from '../../../../../store/common/common.actions'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import { DeletePostRequest, PublishPostRequest } from '@3m5/sm_publish-frontend-api'
import {
  ConfirmManualDeferredPostRequest,
  DeclineManualDeferredPostRequest,
  DeleteManualDeferredPostRequest,
  RetryManualDeferredPostRequest,
} from '@3m5/sm_publish-frontend-api'
import { PostObject } from '../ProfessocialCalendar'
import NetworkContentTabs
  from '@professocial/professocialTopics/src/shared/components/ModuleComponents/NetworkContentTabs'

import Google from '@mui/icons-material/Google'
import CheckIcon from '@mui/icons-material/Check'
import Instagram from '@mui/icons-material/Instagram'
import Facebook from '@mui/icons-material/Facebook'
import { GetFacebookPostRequest, PostContent } from '@3m5/sm_publish-frontend-api'
import { GetInstagramPostRequest } from '@3m5/sm_publish-frontend-api'
import VerticalSplitOutlined from '@mui/icons-material/VerticalSplitOutlined'
import Headline from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/Headline'
import LinkHeadline
  from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/LinkHeadline'
import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import DashboardNetworkContentPostDetailsTable
  from '../../../../../screens/Editor/EditorDashboard/components/DashboardNetworkContentPostDetailsTable'
import { FilterType } from '@professocial/professocialPosting/src/shared/types/types'

interface IProps extends AllHTMLAttributes<any> {
  post: PostContent;
  postMetaData?: PostObject;
  published: boolean;
  disabled?: boolean;
  isOpen: boolean;
  setSelectedDate?: (selectedDate: number) => void;
  editSettings?: (topicId: string | undefined, aboType: AboType) => void;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'inherit',
    paddingTop: '9px',
    width: '100%',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'fixed',
    top: 86,
    left: '0',
    height: 'calc(100vh - 86px)',
    overflow: 'auto',
    width: '100vw',
    backgroundColor: 'rgba(0,0,0,0.7)',
    cursor: 'pointer',
    zIndex: 8,
    [theme.breakpoints.up('md')]: {
      top: 100,
      height: 'calc(100vh - 128px)',
    },
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  image: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
  grid: {
    width: '90vw',
    maxWidth: '1000px',
    height: 'calc(75vh - 86px)',
    [theme.breakpoints.up('md')]: {
      height: 'calc(85vh - 128px)',
    },
  },
  closeIcon: {
    alignSelf: 'flex-end',
    color: '#fff',
    marginRight: '-15px',
  },
  button: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'initial',
  },
  icon: {
    position: 'absolute',
    right: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    width: '35px',
    height: '35px',
    backgroundColor: theme.palette.primary.main,
  },
  backgroundLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgb(0,0,0,0.6)',
  },
  preloader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
  },
  error: {
    color: theme.palette.error.main,
    display: 'flex',
  },
}))

const CalendarLightbox: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()

  const storedFacebookPageId: string | null = window.localStorage.getItem('facebookPageNetworkId')
  const storedInstagramPageId: string | null = window.localStorage.getItem('instagramPageNetworkId')
  const storedGoogleMyBusinessPageId: string | null = window.localStorage.getItem('googleMyBusinessPageNetworkId')

  const storedFacebookPage: FacebookPage | undefined = useSelector(getFacebookPage)
  const storedInstagramPage: InstagramPage | undefined = useSelector(getInstagramPage)
  const calendarResponseData: CalendarData | undefined = useSelector(getCalendarData)
  const aboTopics: TopicsWithMetadataResponse | undefined = useSelector(getAboTopics)

  const [networkPage, setNetworkPage] = useState<NetworkPage>({})
  const [publishedPost, setPublishedPost] = useState<PublishedPostWithPostContent | undefined>()
  const [deferredPost, setDeferredPost] = useState<DeferredPost | undefined>()
  const [template, setTemplate] = useState<PostTemplate | undefined>()

  const storedNetworkPostId = publishedPost?.publishedPost.networkPostId
  const storedFacebookPost: FacebookPost | undefined = useSelector(selectFacebookPostByPostId(storedNetworkPostId))
  const storedInstagramPost: InstagramPost | undefined = useSelector(selectInstagramPostByPostId(storedNetworkPostId))

  useEffect(() => {
    setNetworkPage({
      ...networkPage,
      facebookPage: storedFacebookPage,
    })
  }, [storedFacebookPage],
  )

  useEffect(() => {
    setNetworkPage({
      ...networkPage,
      instagramPage: storedInstagramPage,
    })
  }, [storedInstagramPage],
  )

  const [isOpen, setIsOpen] = React.useState<boolean>(props.isOpen)
  const [postMetaData, setPostMetaData] = React.useState<PostObject | undefined>(props.postMetaData)
  // eslint-disable-next-line no-unused-vars
  const [toast, setToast] = React.useState<any>()
  const [tabs, setTabs] = React.useState<NetworkType[]>([])
  const [activeTab, setActiveTab] = React.useState<NetworkType | undefined>(tabs && tabs[0])
  const [showDeleteQuestion, setShowDeleteQuestion] = React.useState<boolean>(false)

  const dispatchRetryManualDeferredPost = useCallback((params: RetryManualDeferredPostRequest) => dispatch(retryManualDeferredPostInitiated(params)), [dispatch])
  const dispatchDeletePostTemplate = useCallback((params: DeletePostTemplateRequest) => dispatch(deletePostTemplateInitiated(params)), [dispatch])
  const dispatchPublishPost = useCallback((params: PublishPostRequest) => dispatch(publishPostInitiated(params)), [dispatch])
  const dispatchDeclineManualDeferredPost = useCallback((params: DeclineManualDeferredPostRequest) => dispatch(declineManualDeferredPostInitiated(params)), [dispatch])
  const dispatchConfirmManualDeferredPost = useCallback((params: ConfirmManualDeferredPostRequest) => dispatch(confirmManualDeferredPostInitiated(params)), [dispatch])
  const dispatchDeleteManualDeferredPost = useCallback((params: DeleteManualDeferredPostRequest) => dispatch(deleteManualDeferredPostInitiated(params)), [dispatch])
  const dispatchDeletePost = useCallback((params: DeletePostRequest) => dispatch(deletePostInitiated(params)), [dispatch])

  const dispatchGetFacebookPost = useCallback((params: GetFacebookPostRequest) => dispatch(getFacebookPostInitiated(params)), [dispatch])
  const dispatchGetInstagramPost = useCallback((params: GetInstagramPostRequest) => dispatch(getInstagramPostInitiated(params)), [dispatch])

  const loadingTemplateDelete: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, getFacebookConnectDataInitiated.type))

  const user = useSelector(getUser)

  const toggleIsOpen = () => {
    props.setIsOpen && props.setIsOpen(!isOpen)
  }

  const countDeferredPostsAwaitingUserConfirmation = (postTemplate: PostTemplate): number => {
    const filter = calendarResponseData?.deferredPosts.filter(
      (deferredPost: DeferredPost) =>
        deferredPost.templateId === postTemplate.id &&
        deferredPost.status === DeferredPostStatus.AwaitingUserConfirmation
    )
    return filter?.length || 0
  }

  const countOtherDeferredPosts = (postTemplate: PostTemplate): number => {
    const filter = calendarResponseData?.deferredPosts.filter(
      (deferredPost: DeferredPost) =>
        deferredPost.templateId === postTemplate.id &&
        deferredPost.status !== DeferredPostStatus.AwaitingUserConfirmation
    )
    return filter?.length || 0
  }

  const getAboTopicTypeForTemplate = (postTemplate: PostTemplate): string | undefined => {
    const foundTopic = aboTopics?.topics.find((topic) => topic.topic.id === postTemplate.topicId)
    if (!foundTopic) return undefined
    if (!foundTopic.topic.automaticPostingAllowed && !foundTopic.topic.manualPostingAllowed) {
      return t('coreCalendar:category')
    }
    return t('coreCalendar:subscribableTopic')
  }

  const getFilterTypeForTemplate = (postTemplate: PostTemplate): FilterType | undefined => {
    const foundTopic = aboTopics?.topics.find((topic) => topic.topic.id === postTemplate.topicId)
    if (!foundTopic) return undefined
    if (!foundTopic.topic.automaticPostingAllowed && !foundTopic.topic.manualPostingAllowed) {
      return FilterType.template
    }
    return FilterType.topic
  }

  const aboTopicType = useMemo(() => {
    if (!template) return undefined
    const foundTopic = aboTopics?.topics.find((topic) => topic.topic.id === template.topicId)
    if (!foundTopic) return undefined
    if (!foundTopic.topic.automaticPostingAllowed && !foundTopic.topic.manualPostingAllowed) {
      return t('coreCalendar:category')
    }
    return t('coreCalendar:subscribableTopic')
  }, [template, aboTopics, t])

  useEffect(() => {
    setPostMetaData(props.postMetaData)
  }, [props.postMetaData],
  )

  useEffect(() => {
    setIsOpen(props.isOpen)
  }, [props.isOpen],
  )

  useEffect(() => {
    let actualTabs: NetworkType[] = []
    // Ausblenden der Tabs, die keine posts enthalten
    if ((postMetaData?.published && postMetaData?.published.length > 0) || (postMetaData?.deferred && postMetaData?.deferred.length > 0)) {
      postMetaData?.published.map(elem => {
        actualTabs.push(elem.publishedPost.networkType)
      })
      postMetaData?.deferred.map(elem => {
        actualTabs.push(elem.networkType)
      })
      actualTabs = actualTabs.filter((item, index) => actualTabs.indexOf(item) === index)
    }

    // Sortieren
    const sortedTabs: NetworkType[] = []
    if (actualTabs.find(elem => elem === NetworkType.Facebook)) {
      sortedTabs.push(NetworkType.Facebook)
    }
    if (actualTabs.find(elem => elem === NetworkType.Instagram)) {
      sortedTabs.push(NetworkType.Instagram)
    }
    if (actualTabs.find(elem => elem === NetworkType.GoogleMyBusiness)) {
      sortedTabs.push(NetworkType.GoogleMyBusiness)
    }
    setTabs(sortedTabs)
    sortedTabs.length !== 0 && setActiveTab(sortedTabs[0])
  }, [postMetaData],
  )

  useEffect(() => {
    if (isOpen) {
      let published = postMetaData?.published.find(elem => elem.publishedPost.networkType === activeTab)
      let deferred = postMetaData?.deferred.find(elem => elem.networkType === activeTab)

      if (published !== undefined) {
        if (published.postContent === undefined) {
          published = {
            ...published,
            postContent: postMetaData?.postContent,
          }
        }
        setPublishedPost(published)
        // Post Daten aus dem Netzwerk nachladen
        if (published.publishedPost.networkType === NetworkType.Facebook) {
          const params: GetFacebookPostRequest = {
            networkPageId: published.publishedPost.networkPageId,
            networkPostId: published.publishedPost.networkPostId,
          }
          dispatchGetFacebookPost(params)
        }
        // TODO why instagram tab is showing, when no publish post for network exists?

        if (published.publishedPost.networkType === NetworkType.Instagram) {
          const params: GetInstagramPostRequest = {
            networkPageId: published.publishedPost.networkPageId,
            networkPostId: published.publishedPost.networkPostId,
          }
          dispatchGetInstagramPost(params)
        }
      } else {
        setPublishedPost(undefined)
      }
      if (deferred !== undefined) {
        if (deferred.overridingPostContent === undefined) {
          deferred = {
            ...deferred,
            overridingPostContent: postMetaData?.postContent,
          }
        }
        setDeferredPost(deferred)
      } else {
        setDeferredPost(undefined)
      }
      setTemplate(postMetaData?.template)
    }
  }, [activeTab, postMetaData, isOpen],
  )

  useEffect(() => {
    const published = postMetaData?.published.find(elem => elem.publishedPost.networkType === activeTab)
    // Post Daten aus dem Netzwerk nachladen
    if (published?.publishedPost.networkType === NetworkType.Facebook) {
      const params: GetFacebookPostRequest = {
        networkPageId: published.publishedPost.networkPageId,
        networkPostId: published.publishedPost.networkPostId,
      }
      dispatchGetFacebookPost(params)
    }
    if (published?.publishedPost.networkType === NetworkType.Instagram) {
      const params: GetInstagramPostRequest = {
        networkPageId: published.publishedPost.networkPageId,
        networkPostId: published.publishedPost.networkPostId,
      }
      dispatchGetInstagramPost(params)
    }
  }, [],
  )

  return (
    <Box>
      <div className={classes.container}>
        <div className={classes.wrapper}>
          <IconButton
            className={`${classes.closeIcon}`}
            color="default"
            component="div"
            onClick={toggleIsOpen}
            size="large"
          >
            <CancelRounded/>
          </IconButton>
          <div className={classes.grid} onClick={(e) => e.stopPropagation()}>
            <Grid container
              sx={{
                backgroundColor: theme.palette.background.paper,
                padding: theme.spacing(3),
                cursor: 'default',
                position: 'relative'
              }}>
              {tabs && tabs.length > 1
                ? (
                  <div style={{ marginTop: '-30px' }}>
                    <NetworkContentTabs
                      activeItem={activeTab} tabs={tabs} onChangeTab={(tab: NetworkType) => {
                        setActiveTab(tab)
                      }}
                    />
                  </div>
                )
                : (
                  <>
                    {(publishedPost && publishedPost.publishedPost.networkType === NetworkType.Facebook)
                      || (deferredPost && deferredPost.networkType === NetworkType.Facebook) &&
                      <Box className={classes.icon}><Facebook sx={{ color: '#FFF' }}/></Box>}
                    {(publishedPost && publishedPost.publishedPost.networkType === NetworkType.Instagram)
                      || (deferredPost && deferredPost.networkType === NetworkType.Instagram) &&
                      <Box className={classes.icon}><Instagram sx={{ color: '#FFF' }}/></Box>}
                    {(publishedPost && publishedPost.publishedPost.networkType === NetworkType.GoogleMyBusiness)
                      || (deferredPost && deferredPost.networkType === NetworkType.GoogleMyBusiness) &&
                      <Box className={classes.icon}><Google sx={{ color: '#FFF' }}/></Box>}
                  </>
                )}
              <Grid container item sx={{ position: 'relative' }}>
                {publishedPost &&
                  <div style={{ width: '100%' }}>
                    <LinkHeadline
                      networkPageId={publishedPost.publishedPost.networkPageId}
                      post={props.post}
                      networkType={publishedPost?.publishedPost.networkType}
                      value={{
                        url: publishedPost?.publishedPost.networkType === NetworkType.Facebook
                          ? `https://www.facebook.com/${publishedPost.publishedPost.networkPostId}`
                          : `https://www.instagram.com/${storedInstagramPage?.pageName}`,
                        label: publishedPost?.publishedPost.networkType === NetworkType.Facebook ? storedFacebookPage?.pageName : storedInstagramPage?.pageName,
                      }}
                    />
                  </div>}
                {(!deferredPost?.templateId && !publishedPost?.publishedPost.templateId && user?.role === 'USER' && (deferredPost || publishedPost)) &&
                  <Headline
                    networkPageId={deferredPost?.networkPageId}
                    post={props.post}
                    networkType={deferredPost?.networkType}
                    value={t('coreCalendar:ownPosting')}
                  />}
                {publishedPost &&
                  <ImportProfessocialPost
                    postConfiguration={{
                      theme: theme,
                      userRole: user?.role,
                      size: ScreenEnum.xs,
                      view: ViewEnum.template,
                      networkType: activeTab,
                      publishedPost: publishedPost,
                      post: template,
                    }}
                  />}
                {deferredPost &&
                  <ImportProfessocialPost
                    postConfiguration={{
                      theme: theme,
                      userRole: user?.role,
                      size: ScreenEnum.xs,
                      view: ViewEnum.template,
                      networkType: activeTab,
                      deferredPost: deferredPost,
                      post: template,
                    }}
                  />}
                {!publishedPost && !deferredPost && template &&
                  <>
                    { getAboTopicTypeForTemplate(template) && <Chip size='small' color='primary' label={aboTopicType} /> }
                    <ImportProfessocialPost
                      postConfiguration={{
                        theme: theme,
                        userRole: user?.role,
                        size: ScreenEnum.xs,
                        view: ViewEnum.template,
                        post: template,
                        filterType: getFilterTypeForTemplate(template),
                      }}
                    />
                  </>}
              </Grid>
              <Grid xs={12} item>
                {/* published Posts */}
                {user?.role === 'USER' && props.published && (
                  <>
                    <Divider sx={{ my: 0.5 }}/>
                    <Stack direction="row" justifyContent="space-between" alignItems="center"
                      spacing={2}>
                      <Box sx={{ display: 'flex' }}>
                        {activeTab === NetworkType.Facebook &&
                          <Statistic
                            post={storedFacebookPost}
                            view={ViewEnum.single}
                            justifyContent="start"
                            networkType={NetworkType.Facebook}
                            value={{
                              postEngagedUsers: storedFacebookPost?.postStatistics?.postEngagedUsers,
                              postReactionsLikeTotal: storedFacebookPost?.postStatistics?.postReactionsLikeTotal,
                              postImpressions: storedFacebookPost?.postStatistics?.postImpressions
                            }}
                          />
                        }
                        {activeTab === NetworkType.Instagram &&
                          <Statistic
                            post={storedInstagramPost}
                            view={ViewEnum.single}
                            justifyContent="start"
                            networkType={NetworkType.Instagram}
                            value={{
                              commentCount: storedInstagramPost?.postStatistics?.commentCount,
                              engagement: storedInstagramPost?.postStatistics?.engagement,
                              impressions: storedInstagramPost?.postStatistics?.impressions,
                              likeCount: storedInstagramPost?.postStatistics?.likeCount,
                              reach: storedInstagramPost?.postStatistics?.reach,
                            }}
                          />}
                      </Box>
                      {((!storedFacebookPost && publishedPost?.publishedPost.networkType === NetworkType.Facebook && activeTab === NetworkType.Facebook) ||
                          (!storedInstagramPost && publishedPost?.publishedPost.networkType === NetworkType.Instagram && activeTab === NetworkType.Instagram)) &&
                        <div style={{ width: '200px' }}>
                          {t('coreDashboard:postDeleted')}
                        </div>}
                      {publishedPost?.publishedPost.networkType === NetworkType.Facebook && storedFacebookPost &&
                        <Stack direction="row" justifyContent="flex-end" alignItems="center"
                          spacing={2}>
                          {showDeleteQuestion
                            ? (
                              <>
                                <Typography>{t('coreCalendar:reallyDelete')}</Typography>
                                <IconButton onClick={() => {
                                  setShowDeleteQuestion(false)
                                  if (publishedPost?.publishedPost.networkPageId && publishedPost?.publishedPost.networkPostId) {
                                    const params: DeletePostRequest = {
                                      networkType: NetworkType.Facebook,
                                      networkPageId: publishedPost?.publishedPost.networkPageId,
                                      networkPostId: publishedPost?.publishedPost.networkPostId,
                                    }
                                    dispatchDeletePost(params)
                                  }
                                }}
                                >
                                  <CheckIcon/>
                                </IconButton>
                                <IconButton
                                  onClick={() => setShowDeleteQuestion(false)}><ClearIcon/></IconButton>
                              </>)
                            : (
                              <TooltipComponent
                                title={<>{t('coreCalendar:deletePost')}</>}
                                child={
                                  <IconButton
                                    onClick={() => setShowDeleteQuestion(true)}>
                                    <DeleteIcon/>
                                  </IconButton>
                                }
                              />)}
                        </Stack>}
                    </Stack>
                    <Divider sx={{ my: 0.5 }}/>
                  </>
                )}
                {/* deferredPosts */}
                {user?.role === 'USER' && !props.published && (
                  <>
                    <Divider sx={{ my: 0.5 }}/>
                    <Stack direction="row" justifyContent="space-between" alignItems="center"
                      spacing={2}>
                      {/* Status anzeigen */}
                      <Box sx={{ width: '380px' }}>
                        {deferredPost?.status &&
                          <Typography
                            className={`${deferredPost?.status &&
                            (deferredPost?.status === DeferredPostStatus.TokenInvalid ||
                              deferredPost?.status === DeferredPostStatus.ErrorWhilePosting ||
                              deferredPost?.status === DeferredPostStatus.NoActiveUsersForPage) && classes.error}`}
                          >
                            {deferredPost?.status && (deferredPost?.status === DeferredPostStatus.TokenInvalid || deferredPost?.status === DeferredPostStatus.ErrorWhilePosting || deferredPost?.status === DeferredPostStatus.NoActiveUsersForPage) &&
                              <WarningIcon style={{ margin: '0 5px' }}/>}
                            <strong>{`${t(`coreCalendar:deferredPostStatus${deferredPost?.status}`)}`}</strong>
                          </Typography>}
                      </Box>
                      <Stack direction="row" justifyContent="flex-end" alignItems="center"
                        spacing={2}>
                        {(deferredPost?.status === DeferredPostStatus.TokenInvalid || deferredPost?.status === DeferredPostStatus.ErrorWhilePosting) &&
                          <Box>
                            <ButtonComponent
                              variant="text"
                              color="info"
                              className={classes.button}
                              text={t('coreCalendar:retry')}
                              onClick={() => {
                                const params: RetryManualDeferredPostRequest = {
                                  deferredPostId: deferredPost?.id,
                                }
                                dispatchRetryManualDeferredPost(params)
                              }}
                            />
                          </Box>}
                        {/* abonnierbare Posts */}
                        {postMetaData?.template?.topicId &&
                          <>
                            {/* noch nicht abonnierte, aber abonnierbare Posts */}
                            {((deferredPost && deferredPost.networkType !== activeTab) || (!deferredPost)) &&
                              <ButtonComponent
                                variant="text"
                                color="info"
                                className={classes.button}
                                text={
                                  <>
                                    <VerticalSplitOutlined
                                      style={{ fontSize: '26px' }}/>
                                    <Typography>{t('coreCalendar:takeTemplate')}</Typography>
                                  </>
                                }
                                onClick={() => {
                                  const networkMaps: NetworkMap[] = []
                                  if (activeTab === NetworkType.Facebook && (storedFacebookPageId)) {
                                    networkMaps.push({
                                      networkType: NetworkType.Facebook,
                                      pages: [storedFacebookPageId || ''],
                                    })
                                  }
                                  if (activeTab === NetworkType.Instagram && (storedInstagramPageId)) {
                                    networkMaps.push({
                                      networkType: NetworkType.Instagram,
                                      pages: [storedInstagramPageId || ''],
                                    })
                                  }
                                  if (activeTab === NetworkType.GoogleMyBusiness && (storedGoogleMyBusinessPageId)) {
                                    networkMaps.push({
                                      networkType: NetworkType.GoogleMyBusiness,
                                      pages: [storedGoogleMyBusinessPageId || ''],
                                    })
                                  }

                                  const params: PublishPostRequest = {
                                    createPostRequestBody: {
                                      postTemplateId: postMetaData?.template?.id,
                                      overridingPostContent: postMetaData?.postContent,
                                      postStart: postMetaData?.template?.postStart,
                                      postEnd: postMetaData?.template?.postEnd,
                                      networkMaps: networkMaps,
                                    },
                                  }
                                  dispatchPublishPost(params)
                                }}
                              />}
                            {/* manuelle Posts oder Posts mit NoActiveUsersForPage Status */}
                            {(deferredPost?.source === DeferredPostSource.ManualAbo || 
                              deferredPost?.status === DeferredPostStatus.NoActiveUsersForPage) &&
                              <>
                                {showDeleteQuestion
                                  ? (
                                    <>
                                      <Typography>{t('coreCalendar:reallyDelete')}</Typography>
                                      <IconButton onClick={() => {
                                        if (deferredPost?.id) {
                                          const params: DeclineManualDeferredPostRequest = {
                                            deferredPostId: deferredPost?.id,
                                          }
                                          dispatchDeclineManualDeferredPost(params)
                                        }
                                        setShowDeleteQuestion(false)
                                      }}
                                      >
                                        <CheckIcon/>
                                      </IconButton>
                                      <IconButton
                                        onClick={() => setShowDeleteQuestion(false)}><ClearIcon/></IconButton>
                                    </>
                                  )
                                  : (
                                    <>
                                      <TooltipComponent
                                        title={<>{t('coreCalendar:confirmPost')}</>}
                                        child={
                                          <IconButton onClick={() => {
                                            if (deferredPost?.id) {
                                              const params: ConfirmManualDeferredPostRequest = {
                                                deferredPostId: deferredPost?.id,
                                              }
                                              dispatchConfirmManualDeferredPost(params)
                                            }
                                          }}
                                          ><TaskAltIcon/>
                                          </IconButton>
                                        }
                                      />
                                      <TooltipComponent
                                        title={<>{t('coreCalendar:deletePost')}</>}
                                        child={
                                          <IconButton
                                            onClick={() => setShowDeleteQuestion(true)}>
                                            <DeleteIcon/></IconButton>
                                        }
                                      />
                                    </>
                                  )}
                              </>}
                            {/* automatische Posts */}
                            {deferredPost?.source === DeferredPostSource.AutomaticAbo && 
                             deferredPost?.status !== DeferredPostStatus.NoActiveUsersForPage &&
                              <>
                                {showDeleteQuestion
                                  ? (
                                    <>
                                      <Typography>{t('coreCalendar:reallyDelete')}</Typography>
                                      <IconButton onClick={() => {
                                        if (deferredPost?.id) {
                                          const params: DeleteManualDeferredPostRequest = {
                                            deferredPostId: deferredPost?.id,
                                          }
                                          dispatchDeleteManualDeferredPost(params)
                                        }
                                        setShowDeleteQuestion(false)
                                      }}
                                      >
                                        <CheckIcon/>
                                      </IconButton>
                                      <IconButton
                                        onClick={() => setShowDeleteQuestion(false)}><ClearIcon/></IconButton>
                                    </>
                                  )
                                  : (
                                    <TooltipComponent
                                      title={<>{t('coreCalendar:deletePost')}</>}
                                      child={
                                        <IconButton
                                          onClick={() => setShowDeleteQuestion(true)}><DeleteIcon/></IconButton>
                                      }
                                    />
                                  )}
                              </>}
                          </>}
                        {/* nicht abonnierbare Posts */}
                        {(!postMetaData?.template?.topicId || deferredPost?.source === DeferredPostSource.IndividualPost) &&
                         deferredPost?.status !== DeferredPostStatus.NoActiveUsersForPage &&
                          <>
                            {showDeleteQuestion
                              ? (
                                <>
                                  <Typography>{t('coreCalendar:reallyDelete')}</Typography>
                                  <IconButton onClick={() => {
                                    if (deferredPost?.id) {
                                      const params: DeleteManualDeferredPostRequest = {
                                        deferredPostId: deferredPost?.id,
                                      }
                                      dispatchDeleteManualDeferredPost(params)
                                    }
                                    setShowDeleteQuestion(false)
                                  }}
                                  >
                                    <CheckIcon/>
                                  </IconButton>
                                  <IconButton
                                    onClick={() => setShowDeleteQuestion(false)}><ClearIcon/></IconButton>
                                </>
                              )
                              : (
                                <TooltipComponent
                                  title={<>{t('coreCalendar:deletePost')}</>}
                                  child={
                                    <IconButton
                                      onClick={() => setShowDeleteQuestion(true)}>
                                      <DeleteIcon/></IconButton>
                                  }
                                />
                              )}
                          </>}
                      </Stack>
                    </Stack>
                    <Divider sx={{ my: 0.5 }}/>
                  </>
                )}
                {/* EDITOR */}
                {user?.role === 'EDITOR' && (
                  <>
                    <Divider sx={{ my: 0.5 }}/>
                    <Grid container item spacing={2}>
                      {(postMetaData?.template?.postStart && new Date(postMetaData?.template?.postStart) < new Date(Date.now())) && !showDeleteQuestion &&
                        (
                          <>
                            <Grid item xs={12} md={6} paddingTop={0} sx={{ paddingTop: 0 }}>
                              <DashboardNetworkContentPostDetailsTable
                                type='general'
                                title={t('coreCalendar:autoPublications')}
                                pending={countOtherDeferredPosts(postMetaData.template)}
                                successful={postMetaData.template.automaticSuccess}
                                failure={postMetaData.template.automaticFail}
                              />
                            </Grid>
                            <Grid item xs={12} md={6} alignItems="flex-end" justifyContent="flex-end" paddingTop={0}>
                              <DashboardNetworkContentPostDetailsTable
                                type='awaiting'
                                title={t('coreCalendar:individualPublications')}
                                pending={countDeferredPostsAwaitingUserConfirmation(postMetaData.template)}
                                successful={postMetaData.template.manualSuccess}
                                failure={postMetaData.template.manualFail}
                              />
                            </Grid>
                            <Grid item xs={12}><Divider/></Grid>
                          </>
                        )}
                      <Grid item xs={12}>
                        {(postMetaData?.template?.postStart && new Date(postMetaData?.template?.postStart) < new Date(Date.now())) &&
                          (
                            // Todo Statistiken noch anbinden
                            <>
                              {!showDeleteQuestion &&
                                <Stack direction="row" justifyContent="space-between"
                                  alignItems="center" spacing={2}>
                                  <Box sx={{ display: 'flex' }}>
                                    {activeTab === NetworkType.Facebook &&
                                      <Statistic
                                        post={storedFacebookPost}
                                        view={ViewEnum.single}
                                        justifyContent="start"
                                        networkType={NetworkType.Facebook}
                                        value={{
                                          postEngagedUsers: storedFacebookPost?.postStatistics?.postEngagedUsers,
                                          postReactionsLikeTotal: storedFacebookPost?.postStatistics?.postReactionsLikeTotal,
                                          postImpressions: storedFacebookPost?.postStatistics?.postImpressions
                                        }}
                                      />
                                    }
                                    {activeTab === NetworkType.Instagram &&
                                      <Statistic
                                        post={storedInstagramPost}
                                        view={ViewEnum.single}
                                        justifyContent="start"
                                        networkType={NetworkType.Instagram}
                                        value={{
                                          commentCount: storedInstagramPost?.postStatistics?.commentCount,
                                          engagement: storedInstagramPost?.postStatistics?.engagement,
                                          impressions: storedInstagramPost?.postStatistics?.impressions,
                                          likeCount: storedInstagramPost?.postStatistics?.likeCount,
                                          reach: storedInstagramPost?.postStatistics?.reach,
                                        }}
                                      />}
                                  </Box>
                                  <Box>
                                    <>
                                      {!showDeleteQuestion &&
                                        <TooltipComponent
                                          title={<>{t('coreCalendar:deletePost')}</>}
                                          child={
                                            <IconButton
                                              onClick={() => setShowDeleteQuestion(true)}><DeleteIcon/></IconButton>
                                          }
                                        />}
                                    </>
                                  </Box>
                                </Stack>}
                              {showDeleteQuestion &&
                                <Stack alignItems="center" spacing={2}
                                  style={{ marginTop: '20px' }}>
                                  <Box><Typography>{t('coreCalendar:deleteTemplateWarning')}</Typography></Box>
                                  <Stack direction="row"
                                    justifyContent="space-between"
                                    alignItems="center" spacing={2}
                                    style={{ width: '100%' }}>
                                    <Box><IconButton
                                      onClick={() => setShowDeleteQuestion(false)}><ClearIcon/></IconButton></Box>
                                    <Stack direction="row"
                                      justifyContent="space-between"
                                      alignItems="center" spacing={2}>
                                      <ButtonComponent
                                        color="secondary" onClick={() => {
                                          if (postMetaData?.template?.id) {
                                            const params: DeletePostTemplateRequest = {
                                              templateId: postMetaData?.template?.id,
                                              deletePublished: false,
                                            }
                                            dispatchDeletePostTemplate(params)
                                          }
                                          setShowDeleteQuestion(false)
                                        }}
                                        text={t('coreCalendar:onlyTemplate')}
                                      />
                                      <ButtonComponent
                                        color="primary" onClick={() => {
                                          if (postMetaData?.template?.id) {
                                            const params: DeletePostTemplateRequest = {
                                              templateId: postMetaData?.template?.id,
                                              deletePublished: true,
                                            }
                                            dispatchDeletePostTemplate(params)
                                          }
                                          setShowDeleteQuestion(false)
                                        }}
                                        text={t('coreCalendar:templateAndPosts')}
                                      />
                                    </Stack>
                                  </Stack>
                                </Stack>}
                            </>
                          )}
                        {(postMetaData?.template?.postStart && new Date(postMetaData?.template?.postStart) > new Date(Date.now())) &&
                          (
                            <Stack direction="row" justifyContent="flex-end"
                              alignItems="center" spacing={2}>
                              <>
                                {showDeleteQuestion ? (
                                  <>
                                    <Typography>{t('coreCalendar:reallyDelete')}</Typography>
                                    <IconButton onClick={() => {
                                      if (postMetaData?.template?.id) {
                                        const params: DeletePostTemplateRequest = {
                                          templateId: postMetaData?.template?.id,
                                          deletePublished: false,
                                        }
                                        dispatchDeletePostTemplate(params)
                                      }
                                      setShowDeleteQuestion(false)
                                    }}
                                    >
                                      <CheckIcon/>
                                    </IconButton>
                                    <IconButton
                                      onClick={() => setShowDeleteQuestion(false)}><ClearIcon/></IconButton>
                                  </>
                                )
                                  : (
                                    <>
                                      <TooltipComponent
                                        title={<>{t('coreCalendar:editTemplate')}</>}
                                        child={
                                          <IconButton onClick={() => {
                                            history.push({
                                              pathname: `/${user?.role.toLowerCase()}/dashboard/posting`,
                                              state: { detail: { data: template, role: 'EDITOR' } },
                                            })
                                          }}
                                          ><EditIcon/>
                                          </IconButton>
                                        }
                                      />
                                      <TooltipComponent
                                        title={<>{t('coreCalendar:deletePost')}</>}
                                        child={
                                          <IconButton
                                            onClick={() => setShowDeleteQuestion(true)}><DeleteIcon/></IconButton>
                                        }
                                      />
                                    </>
                                  )}
                              </>
                            </Stack>
                          )}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            {loadingTemplateDelete &&
              <>
                <div className={classes.backgroundLayer}/>
                <div className={classes.preloader}><Preloader color="secondary"/></div>
              </>}
          </div>
        </div>
      </div>
      {toast && (
        <DialogComponent
          type={toast.type || undefined}
          autoClose={toast.autoClose}
          title={toast.title}
          message={toast.message}
          open={toast.open}
          confirmLabel={toast.confirmLabel}
          cancelLabel={toast.cancelLabel}
          requestActions={toast.requestActions}
          onClose={toast.handleClose}
          onCancel={toast.handleCancel}
          onConfirm={toast.handleConfirm}
        />
      )}
    </Box>
  )
}

export default CalendarLightbox
